import { OrderLineItem, usdToPoints } from '@models';
import type { AppleCareItem } from '../services/product-capabilities.service';

const buildAppleCareItem = (appleCareLineItem: OrderLineItem, parentItem: OrderLineItem): AppleCareItem => {
  return {
    id: appleCareLineItem.id,
    productName: appleCareLineItem.name,
    // For display: use unit price and calculated points
    price: appleCareLineItem.price,
    points: appleCareLineItem.amountPoints || usdToPoints(appleCareLineItem.price),
    brand: appleCareLineItem.brandName || 'Apple',
    model: parentItem.name,
    productId: appleCareLineItem.productId,
    isAdded: true,
    // For totals calculation: use actual payment amounts from backend
    subtotalPoints: appleCareLineItem.total?.subtotalPoints || 0,
    subtotalPrice: appleCareLineItem.total?.subtotalPrice || 0,
  };
};

/**
 * Builds a map of parent line item ID -> AppleCare item for an order.
 * Uses the relatedLineItemId from the API to establish relationships.
 * AppleCare items with a broken/missing relatedLineItemId (RID-1144) are
 * attached to the first suitable parent as a fallback, so they are never
 * silently dropped from "Your items" and the total.
 */
export const buildOrderAppleCareItemsMap = (
  lineItems: OrderLineItem[],
  appleCareProductIds: number[],
): Map<string, AppleCareItem> => {
  const map = new Map<string, AppleCareItem>();

  const appleCareLineItems = lineItems.filter((item) => appleCareProductIds.includes(item.productId));
  if (!appleCareLineItems.length) {
    return map;
  }
  const parentLineItems = lineItems.filter((item) => !appleCareProductIds.includes(item.productId));

  const attachedAppleCareIds = new Set<string>();

  // Attach by the explicit relation from the API
  parentLineItems.forEach((item) => {
    const appleCareForThisItem = appleCareLineItems.find((ac) => ac.relatedLineItemId === item.id);

    if (appleCareForThisItem) {
      map.set(item.id, buildAppleCareItem(appleCareForThisItem, item));
      attachedAppleCareIds.add(appleCareForThisItem.id);
    }
  });

  // Fallback for broken relations: attach orphaned AppleCare items to the
  // first parent without one, preferring Apple-branded products
  const orphanedAppleCareItems = appleCareLineItems.filter((ac) => !attachedAppleCareIds.has(ac.id));

  orphanedAppleCareItems.forEach((appleCareLineItem) => {
    const candidates = parentLineItems.filter((item) => !map.has(item.id));
    const appleCandidates = candidates.filter((item) => item.brandName?.toLowerCase() === 'apple');
    const parent = appleCandidates[0] ?? (candidates.length === 1 ? candidates[0] : undefined);

    if (parent) {
      map.set(parent.id, buildAppleCareItem(appleCareLineItem, parent));
    }
  });

  return map;
};
